import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { CategorySupplierProps } from '../types';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { type NumberFormatValues, NumericFormat, type SourceInfo } from 'react-number-format';

import scss from './ReviewList.module.scss';
import Rating from '../Rating';
import { BulkReviewsContext, type BulkReviewsContextType } from '../context';
import { deepCopy } from '@helper/objectHelper';
import { getCurrencySymbol } from '@helper/getConfigValues';
import ConfirmationModal from "@ui/ConfirmationModal";

const WORD_COUNT: number = 15;

const CategorySupplierExpanded = ({ category, review }: CategorySupplierProps) => {
    const { showValidationMessages, addUpdateReview, deleteReview } = useContext<BulkReviewsContextType>(BulkReviewsContext);

    const [rating, setRating] = useState<number>(!!review ? review.userStarRating : 0);
    const [amount, setAmount] = useState<number | undefined>(review?.userSupplierSpend);
    const [comments, setComments] = useState<string>(!!review?.userReview ? review?.userReview : '');
    const [isMinimumWordsMet, setIsMinimumWordsMet] = useState<boolean>(!!review?.userReview ? review?.userReview.split(/\s+/).filter(word => word !== '').length >= WORD_COUNT : false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const currencySymbol = getCurrencySymbol();

    useEffect(() => {
        if (rating > 0 && isMinimumWordsMet && !review?.complete) {
            handleSaveReview(isComplete(rating, isMinimumWordsMet, amount), rating, amount, comments);
        }
    }, [review, rating, isMinimumWordsMet, amount]);

    const isComplete = (score: number, minWordsMet: boolean, spend: number | undefined) => {
        return score > 0 && minWordsMet && !!spend;
    }

    const handleRatingChange = (value: number) => {
        setRating(value);
        handleSaveReview(isComplete(value, isMinimumWordsMet, amount), value, amount, comments);
    };

    const handleCommentBlur = (text: string) => {
        handleCommentsUpdate(text);
        handleSaveReview(isComplete(rating, isMinimumWordsMet, amount), rating, amount, text);
    };

    const handleAmountChange = (values: NumberFormatValues, _: SourceInfo) => {
        setAmount(values.floatValue);
        handleSaveReview(isComplete(rating, isMinimumWordsMet, values.floatValue), rating, values.floatValue, comments);
    };

    const handleSaveReview = (isComplete: boolean, score: number, price: number | undefined, text: string) => {
        if (!!review && !!addUpdateReview) {
            const updatedReview = deepCopy(review);
            updatedReview.userReview = !!text ? text : comments;
            updatedReview.userStarRating = score;
            updatedReview.userSupplierSpend = price;
            updatedReview.expanded = true;
            updatedReview.complete = isComplete;
            addUpdateReview(updatedReview);
        }        
    };

    const handleDeleteClick = useCallback(() => {
        setShowDeleteModal(true);
    }, []);
    
    const handleCancelDelete = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    const handleConfirmDelete = () => {
        if (!!review?.id && !!deleteReview) {
            deleteReview(review.id);
        }
        setShowDeleteModal(false);
    };

    const handleClose = () => {
        if (!!review && !!addUpdateReview) {
            const updatedReview = deepCopy(review);
            updatedReview.expanded = false;
            addUpdateReview(updatedReview);
        }
    };

    const handleCommentsUpdate = (text: string) => {
        const wordCount = text.split(/\s+/).filter(word => word !== '').length;
        setIsMinimumWordsMet(wordCount >= WORD_COUNT);
        setComments(text);
    };

    return (
        <div data-testid="expandedSupplier" data-supplierid={review?.supplierId} data-catid={category.categoryId} className={`mt-3 ${review?.expanded ? '' : 'd-none'}`}>
            <div className={scss.ratingCommentsContainer}>
                <Rating initialRating={review?.userStarRating} showWarning={!review?.userStarRating && showValidationMessages} onRatingChange={handleRatingChange} />
                <div>
                    Tell us what the supplier did well and why you would recommend them to others <span className={'text-danger'}>*{!isMinimumWordsMet && showValidationMessages && <small > This field is required</small>}</span>
                </div>
                <Form.Group className={scss.reviewText} controlId="formBasicEmail">
                    <Form.Control
                        as="textarea"
                        rows={5}
                        value={comments}
                        onChange={(event) => handleCommentsUpdate(event.target.value)}
                        onBlur={(event) => handleCommentBlur(event.target.value)}
                        data-testid="supplierReviewComment"
                    />
                    {!isMinimumWordsMet && <small className={scss.minimumWordsError} data-testid="supplierReviewCommentLength">
                        Please enter at least {WORD_COUNT} words.
                    </small>}
                </Form.Group>
                <div className={scss.spendContainer}>
                        <div className="d-inline-block">
                            Spend with this supplier<span className={'text-danger'}>*</span>
                            <OverlayTrigger
                                placement={'top'}
                                trigger={['click', 'hover', 'focus']}
                                overlay={
                                    <Tooltip id={'tooltip-cost-info'}>
                                        We only collect this information for internal research purposes as it helps us provide average wedding cost for future couples.
                                    </Tooltip>
                                }
                            >
                                <Icon icon={faInfoCircle} className={`ms-2 ${scss.infoIcon}`} />
                            </OverlayTrigger>
                        </div>
                        <NumericFormat
                            className={'form-control'}
                            maxLength={8}
                            thousandSeparator={true}
                            prefix={currencySymbol}
                            value={amount}
                            onValueChange={handleAmountChange}
                            data-testid="supplierReviewSpend"
                        />
                        {!review?.userSupplierSpend && showValidationMessages && <small className={'text-danger'}> This field is required</small>}                        
                </div>       
            </div>
            <div className={scss.actionContainer}>
                <ConfirmationModal
                    text={'Are you sure you would like to delete this review?'}
                    confirmText={'Yes, delete this review'}
                    show={showDeleteModal}
                    onHide={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />
                <Button onClick={handleDeleteClick} variant={'link'} className={scss.removeButton} data-testid="supplierReviewRemove">
                    Delete Review
                    <Icon icon={faTrash} className={'ms-2'} />
                </Button>
                <Button onClick={handleClose} variant={'link'} className={scss.closeButton} data-testid="supplierReviewClose">
                    Close
                </Button>
            </div>
        </div>
    )
}

export default CategorySupplierExpanded